import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Shield, Database, Gauge, FileText, Phone, BarChart3, ArrowRight, Calculator, 
  Users, TrendingUp, CheckCircle2, Sparkles, Smartphone
} from 'lucide-react';

const PDSPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  // Cost Calculator State
  const [costParams, setCostParams] = useState({
    padCost: 4,
    padsPerPack: 8,
    deliveryCost: 4,
    phoneCost: 4000,
    tabletCost: 20000,
    agentSalary: 8000,
    supervisorSalary: 20000,
    numVillagers: 10000,
    timePeriod: 12,
    numAgents: null,
    numSupervisors: null
  });

  const calculateCosts = () => {
    const { padCost, padsPerPack, deliveryCost, phoneCost, tabletCost, agentSalary, supervisorSalary, numVillagers, timePeriod, numAgents, numSupervisors } = costParams;
    
    const calculatedAgents = numAgents !== null ? numAgents : Math.ceil(numVillagers / 1000);
    const calculatedSupervisors = numSupervisors !== null ? numSupervisors : Math.ceil(calculatedAgents / 30);
    
    const monthlyPadCost = (padCost * padsPerPack * numVillagers);
    const monthlyDeliveryCost = (deliveryCost * numVillagers);
    const monthlyAgentSalaries = (agentSalary * calculatedAgents);
    const monthlySupervisorSalaries = (supervisorSalary * calculatedSupervisors);
    const monthlyRecurring = monthlyPadCost + monthlyDeliveryCost + monthlyAgentSalaries + monthlySupervisorSalaries;
    
    const phonesCost = phoneCost * calculatedAgents;
    const tabletsCost = tabletCost * calculatedSupervisors;
    const oneTimeCosts = phonesCost + tabletsCost;
    
    const totalRecurring = monthlyRecurring * timePeriod;
    const grandTotal = oneTimeCosts + totalRecurring;
    const costPerBeneficiary = grandTotal / numVillagers;
    
    const recommendedAgents = Math.ceil(numVillagers / 1000);
    const recommendedSupervisors = Math.ceil(calculatedAgents / 30);
    
    return {
      numAgents: calculatedAgents,
      numSupervisors: calculatedSupervisors,
      recommendedAgents,
      recommendedSupervisors,
      monthlyPadCost,
      monthlyDeliveryCost,
      monthlyAgentSalaries,
      monthlySupervisorSalaries,
      monthlyRecurring,
      phonesCost,
      tabletsCost,
      oneTimeCosts,
      totalRecurring,
      grandTotal,
      costPerBeneficiary
    };
  };

  const costs = calculateCosts();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleInputChange = (field, value) => {
    if (field === 'numAgents' || field === 'numSupervisors') {
      setCostParams(prev => ({ ...prev, [field]: value === '' || value === null ? null : parseFloat(value) || null }));
    } else {
      setCostParams(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
    }
  };

  const benefits = [
    {
      icon: Shield,
      title: 'Branding & Marketing',
      description: 'All distributed sanitary pads carry your branding, acting as a constant, respectful marketing tool within households. Your presence becomes a part of daily life.',
      shortTitle: 'Branding'
    },
    {
      icon: Database,
      title: 'Direct Voter Database',
      description: 'With registered users, we build a direct SMS/call database, ensuring reach to at least 1-2 individuals in 100% of households for seamless communication.',
      shortTitle: 'Database'
    },
    {
      icon: Gauge,
      title: 'Multi-Purpose Platform',
      description: 'The same secure platform can be used to distribute other essential items like spectacles, medicines, or educational materials, creating multiple touchpoints.',
      shortTitle: 'Platform'
    },
    {
      icon: FileText,
      title: 'Integrated Surveys',
      description: 'Conduct regular, low-cost surveys within your constituency for real-time understanding of public sentiment, needs, and satisfaction levels.',
      shortTitle: 'Surveys'
    },
    {
      icon: Phone,
      title: 'Gramtek Connect Centre',
      description: 'We manage the infrastructure and training for a dedicated helpline. You receive the gratitude, data, and reputation of a public servant who listens.',
      shortTitle: 'Helpline'
    },
    {
      icon: BarChart3,
      title: 'Advanced Data Analysis',
      description: 'Go beyond complaints. Receive quarterly sentiment analysis reports measuring hope, frustration, and key concerns to govern by data, not guesswork.',
      shortTitle: 'Analytics'
    }
  ];

  const impactStats = [
    { value: '100,000+', label: 'Women Empowered Monthly' },
    { value: '800,000+', label: 'Sanitary Pads Distributed' },
    { value: '200+', label: 'Women Agents Employed' },
    { value: '350+', label: 'Villages Covered' }
  ];

  const vrmFeatures = [
    {
      icon: Database,
      title: 'Direct Voter Database',
      description: 'Build comprehensive, verified databases through service delivery touchpoints'
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Insights',
      description: 'Track sentiment, concerns, and satisfaction through data-driven dashboards'
    },
    {
      icon: Users,
      title: 'Community Engagement',
      description: 'Foster meaningful connections through systematic, technology-enabled outreach'
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      description: 'Measure impact and effectiveness of governance initiatives with precision'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1678082309214-3b2941e387f8)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        <div className="relative z-10 container-custom text-center">
          <div className="inline-block bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4 inline mr-2" />
            Gramtek Empowerment Solution
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Gramtek-Public<br />Distribution System
          </h1>
          <p className="text-2xl text-gray-300 mb-4">
            Empower Women. Build Trust. Transform Communities.
          </p>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed text-gray-400">
            A technology-driven platform by AEPL dedicated to building meaningful, data-driven presence for public servants in rural communities through women-led distribution.
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
            Our Mission
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-gray-900">
            Technology That Serves Rural India
          </h2>
          <div className="space-y-6">
            <p className="text-xl text-gray-600 leading-relaxed">
              Gramtek represents our commitment to bringing world-class technology solutions to rural India. We recognize that rural communities have unique needs, challenges, and opportunities that require specialized approaches.
            </p>
            <p className="text-xl text-gray-600 leading-relaxed">
              Our solutions are built on the foundation of <strong className="text-gray-900">Voter Relationship Management (VRM)</strong> — a comprehensive approach that helps elected representatives maintain meaningful, data-driven connections with their constituents through essential service delivery.
            </p>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="section-padding bg-slate-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="container-custom relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-cyan-400 text-sm font-medium mb-4">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <span className="uppercase tracking-wider">See It In Action</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Watch How <span className="text-cyan-400">Gramtek Works</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              See our women agents in action, delivering dignity and building trust across rural communities.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-800 border border-slate-700">
              <div className="aspect-video">
                <iframe 
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/-GGQacT7ado?si=vzvAeJB_XV6us9Zt" 
                  title="Gramtek - Public Distribution System in Action"
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  referrerPolicy="strict-origin-when-cross-origin" 
                  allowFullScreen
                />
              </div>
            </div>
            
            <div className="mt-8 grid grid-cols-3 gap-4 text-center">
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="text-2xl font-bold text-cyan-400">100K+</div>
                <div className="text-xs text-slate-400">Beneficiaries Served</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="text-2xl font-bold text-cyan-400">200+</div>
                <div className="text-xs text-slate-400">Women Agents</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="text-2xl font-bold text-cyan-400">352</div>
                <div className="text-xs text-slate-400">Villages Covered</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-white mesh-gradient">
        <div className="container-custom max-w-4xl">
          <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
            The Challenge
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Access to Essential Hygiene
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-6">
            In rural Maharashtra, access to basic hygiene products like sanitary pads remains a significant challenge. This lack of access leads to serious health complications, social stigma, and limited educational and economic opportunities for women.
          </p>
          <p className="text-xl text-gray-600 leading-relaxed">
            Traditional distribution methods are inefficient, lack accountability, and fail to create meaningful connections between public servants and constituents.
          </p>
        </div>
      </section>

      {/* Solution Overview */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              Our Solution
            </div>
            <h2 className="text-5xl font-bold mb-6 text-gray-900">
              Women-Led Distribution Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              OTP-based, door-to-door distribution with digital tracking that empowers communities while building political capital.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl p-12 shadow-xl border border-gray-100">
              <h3 className="text-3xl font-bold mb-8 text-gray-900">How It Works</h3>
              <div className="space-y-8">
                {[
                  { num: 1, title: 'User Registration', desc: 'Women register through trained Gramtek agents using a secure mobile app with OTP verification.' },
                  { num: 2, title: 'Monthly Distribution', desc: 'Women agents deliver branded sanitary pads directly to doorsteps with OTP-based confirmation.' },
                  { num: 3, title: 'Digital Tracking', desc: 'All deliveries tracked in real-time through our dashboard for complete visibility and accountability.' },
                  { num: 4, title: 'Data & Insights', desc: 'Regular reports provide insights on distribution patterns, satisfaction, and engagement opportunities.' }
                ].map((step, idx) => (
                  <div key={idx} className="flex gap-6 items-start">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
                      {step.num}
                    </div>
                    <div>
                      <h4 className="text-xl font-bold mb-2 text-gray-900">{step.title}</h4>
                      <p className="text-gray-600 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Interface Showcase */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="container-custom relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 text-cyan-400 text-sm font-medium mb-6">
                <Smartphone className="w-4 h-4" />
                <span className="uppercase tracking-wider">Our Technology</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Simple. Accessible. <span className="text-cyan-400">Effective.</span>
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                The Gramtek mobile app is designed for ease of use by women agents in rural areas. With minimal training, agents can manage deliveries, verify beneficiaries, and sync data — even in low connectivity areas.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { title: "OTP Verification", desc: "Secure delivery confirmation" },
                  { title: "Real-time Sync", desc: "Instant dashboard updates" },
                  { title: "Offline Mode", desc: "Works without internet" },
                  { title: "Multi-language", desc: "Regional language support" }
                ].map((feature, i) => (
                  <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                    <p className="text-sm text-slate-400">{feature.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                <p className="text-cyan-300 text-sm">
                  <strong>User-First Design:</strong> Every screen is optimized for quick navigation, with large touch targets and clear visual hierarchy for ease of use in field conditions.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-3xl blur-2xl" />
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_content-hub-780/artifacts/nitbdgm3_Shots%20Mockups%20%283%29.png"
                  alt="Gramtek Mobile App - Agent Dashboard, Delivery Tracking, and Beneficiary Management Interface"
                  className="w-full rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VRM Features */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-white mesh-gradient">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              Core Capabilities
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Voter Relationship Management (VRM)
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our VRM approach transforms how public servants engage with constituents, turning every interaction into an opportunity to serve better and govern smarter.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {vrmFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="modern-card text-left group"
                >
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Benefits with Custom Tabs */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              Strategic Benefits
            </div>
            <h2 className="text-5xl font-bold mb-6 text-gray-900">
              For Public Servants
            </h2>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12 px-4">
            {benefits.map((benefit, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all text-sm sm:text-base ${
                  activeTab === idx
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {benefit.shortTitle}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="max-w-4xl mx-auto px-4">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={idx}
                  className={`${
                    activeTab === idx ? 'block' : 'hidden'
                  } bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 md:p-12 rounded-3xl border-2 border-cyan-100`}
                >
                  <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-gray-900">{benefit.title}</h3>
                      <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="section-padding bg-gradient-to-br from-cyan-500 via-cyan-600 to-blue-600 text-white">
        <div className="container-custom">
          <h2 className="text-5xl font-bold mb-16 text-center">
            Proven Impact
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-6xl font-bold mb-3">{stat.value}</div>
                <div className="text-xl opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Calculator CTA Section */}
      <section id="calculator" className="section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-16 px-4">
            <div className="inline-block bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Calculator className="w-4 h-4 inline mr-2" />
              Investment Calculator
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Calculate Your Constituency Investment
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Get a detailed cost analysis for implementing Gramtek-PDS in your constituency. All costs are based on our successful Ramtek implementation.
            </p>
            <div className="inline-block bg-amber-500/20 border border-amber-500/30 rounded-xl px-6 py-3 max-w-2xl">
              <p className="text-sm font-medium text-amber-200">
                <strong>Real-Life Flow:</strong> Each agent visits their 1,000 assigned beneficiaries door-to-door once every month to distribute 1 pack of sanitary pads per person.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {/* Input Parameters */}
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-white/20">
              <h3 className="text-xl sm:text-2xl font-bold mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-cyan-400" />
                Configure Parameters
              </h3>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-semibold text-gray-200 mb-2 block">
                    Number of Beneficiaries
                  </Label>
                  <Input
                    type="number"
                    value={costParams.numVillagers}
                    onChange={(e) => handleInputChange('numVillagers', e.target.value)}
                    className="h-12 text-lg bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    min="1000"
                    step="1000"
                  />
                </div>

                <div>
                  <Label className="text-base font-semibold text-gray-200 mb-2 block">
                    Implementation Period
                  </Label>
                  <Select value={costParams.timePeriod.toString()} onValueChange={(value) => handleInputChange('timePeriod', value)}>
                    <SelectTrigger className="h-12 text-lg bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Month</SelectItem>
                      <SelectItem value="4">4 Months</SelectItem>
                      <SelectItem value="6">6 Months</SelectItem>
                      <SelectItem value="12">1 Year</SelectItem>
                      <SelectItem value="24">2 Years</SelectItem>
                      <SelectItem value="36">3 Years</SelectItem>
                      <SelectItem value="48">4 Years</SelectItem>
                      <SelectItem value="60">5 Years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-t border-white/20 pt-6">
                  <h4 className="font-semibold text-gray-200 mb-4">Sanitary Pad Parameters</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-300 mb-2 block">Cost per Pad (₹)</Label>
                      <Input
                        type="number"
                        value={costParams.padCost}
                        onChange={(e) => handleInputChange('padCost', e.target.value)}
                        className="h-10 bg-white/10 border-white/20 text-white"
                        min="1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-300 mb-2 block">Pads per Pack</Label>
                      <Input
                        type="number"
                        value={costParams.padsPerPack}
                        onChange={(e) => handleInputChange('padsPerPack', e.target.value)}
                        className="h-10 bg-white/10 border-white/20 text-white"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/20 pt-6">
                  <h4 className="font-semibold text-gray-200 mb-4">Operational Costs</h4>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-gray-300 mb-2 block">Delivery Cost per Person (₹)</Label>
                      <Input
                        type="number"
                        value={costParams.deliveryCost}
                        onChange={(e) => handleInputChange('deliveryCost', e.target.value)}
                        className="h-10 bg-white/10 border-white/20 text-white"
                        min="0"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-300 mb-2 block">Agent Salary (₹/month)</Label>
                      <Input
                        type="number"
                        value={costParams.agentSalary}
                        onChange={(e) => handleInputChange('agentSalary', e.target.value)}
                        className="h-10 bg-white/10 border-white/20 text-white"
                        min="1000"
                      />
                      <p className="text-xs text-gray-400 mt-1">1 Agent manages 1,000 villagers</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-300 mb-2 block">Supervisor Salary (₹/month)</Label>
                      <Input
                        type="number"
                        value={costParams.supervisorSalary}
                        onChange={(e) => handleInputChange('supervisorSalary', e.target.value)}
                        className="h-10 bg-white/10 border-white/20 text-white"
                        min="1000"
                      />
                      <p className="text-xs text-gray-400 mt-1">1 Supervisor manages 30 agents</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/20 pt-6">
                  <h4 className="font-semibold text-gray-200 mb-4">Staffing (Leave empty for auto-calculate)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-300 mb-2 block">Number of Agents</Label>
                      <Input
                        type="number"
                        value={costParams.numAgents === null ? '' : costParams.numAgents}
                        onChange={(e) => handleInputChange('numAgents', e.target.value || null)}
                        placeholder={`Auto: ${costs.recommendedAgents}`}
                        className="h-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        min="0"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-300 mb-2 block">Number of Supervisors</Label>
                      <Input
                        type="number"
                        value={costParams.numSupervisors === null ? '' : costParams.numSupervisors}
                        onChange={(e) => handleInputChange('numSupervisors', e.target.value || null)}
                        placeholder={`Auto: ${costs.recommendedSupervisors}`}
                        className="h-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-6 sm:p-8 shadow-2xl">
                <h3 className="text-2xl font-bold mb-6">Investment Summary</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between pb-3 border-b border-white/20">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      <span className="text-sm sm:text-base">Delivery Agents Needed</span>
                    </div>
                    <span className="text-xl sm:text-2xl font-bold">{costs.numAgents}</span>
                  </div>
                  <div className="flex items-center justify-between pb-3 border-b border-white/20">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      <span className="text-sm sm:text-base">Supervisors Needed</span>
                    </div>
                    <span className="text-xl sm:text-2xl font-bold">{costs.numSupervisors}</span>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 mb-4">
                  <div className="text-sm opacity-90 mb-2">Total Investment for {costParams.timePeriod} months</div>
                  <div className="text-4xl sm:text-5xl font-bold">{formatCurrency(costs.grandTotal)}</div>
                  <div className="text-sm opacity-75 mt-2">Cost per beneficiary: {formatCurrency(costs.costPerBeneficiary)}</div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-white/20">
                <h3 className="text-xl font-bold mb-6">Detailed Cost Breakdown</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-200 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                      One-Time Costs
                    </h4>
                    <div className="space-y-2 pl-7 text-gray-300">
                      <div className="flex justify-between text-sm">
                        <span>5G Phones ({costs.numAgents} units)</span>
                        <span className="font-semibold text-white">{formatCurrency(costs.phonesCost)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Tablets ({costs.numSupervisors} units)</span>
                        <span className="font-semibold text-white">{formatCurrency(costs.tabletsCost)}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-white/20">
                        <span className="text-white">Subtotal</span>
                        <span className="text-cyan-400">{formatCurrency(costs.oneTimeCosts)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-200 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                      Monthly Recurring
                    </h4>
                    <div className="space-y-2 pl-7 text-gray-300">
                      <div className="flex justify-between text-sm">
                        <span>Sanitary Pads</span>
                        <span className="font-semibold text-white">{formatCurrency(costs.monthlyPadCost)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Delivery</span>
                        <span className="font-semibold text-white">{formatCurrency(costs.monthlyDeliveryCost)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Agents ({costs.numAgents})</span>
                        <span className="font-semibold text-white">{formatCurrency(costs.monthlyAgentSalaries)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Supervisors ({costs.numSupervisors})</span>
                        <span className="font-semibold text-white">{formatCurrency(costs.monthlySupervisorSalaries)}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-white/20">
                        <span className="text-white">Monthly Total</span>
                        <span className="text-cyan-400">{formatCurrency(costs.monthlyRecurring)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="text-center pt-4">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-12 py-7 text-xl rounded-2xl shadow-2xl shadow-amber-500/30 group"
                  asChild
                >
                  <Link to="/contact">
                    Get Detailed Proposal
                    <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                <p className="text-gray-400 text-sm mt-4">Schedule a consultation to discuss implementation in your constituency</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PDSPage;
